import { useEffect, useRef, useState } from "react";

import { usePersistedState } from "@swa-ui/persistence";

import { useUserInfo } from "../UserInfoProvider";

/**
 * useAuthenticatedPersistedState stores values into the session storage and retrieves values from the session storage,
 * but only when the user is authenticated. When the user is authenticated for the first time and the state "setter" is
 * invoked, the session storage consists of both the account number and the data associated with it. When using the state
 * "getter", only the relevant data is returned. Can use all the same configuration as `usePersistedState`.
 */

export const useAuthenticatedPersistedState = (configuration) => {
  const { userInfo } = useUserInfo();
  const { "customers.UserInformation.accountNumber": currentAccountNumber } = userInfo ?? {};
  const [authenticatedPersistedState, setAuthenticatedPersistedState] =
    usePersistedState(configuration);
  const [persistedState, setPersistedState] = useState(authenticatedPersistedState?.accountData);
  const defaultValueRef = useRef(configuration?.defaultValue);

  useEffect(() => {
    if (
      currentAccountNumber &&
      currentAccountNumber !== authenticatedPersistedState?.accountNumber
    ) {
      setAuthenticatedPersistedState({ accountData: undefined, accountNumber: undefined });
    }
  }, [currentAccountNumber]);

  useEffect(() => {
    currentAccountNumber &&
      setAuthenticatedPersistedState({
        accountData: persistedState,
        accountNumber: currentAccountNumber,
      });
  }, [persistedState]);

  return [getAuthenticatedData(), setPersistedState];

  function getAuthenticatedData() {
    return currentAccountNumber &&
      currentAccountNumber === authenticatedPersistedState?.accountNumber
      ? authenticatedPersistedState?.accountData
      : defaultValueRef.current;
  }
};
